import { useEffect, useState, useMemo } from 'react';
import Calendar from 'react-calendar';
import toast from 'react-hot-toast';
import { Card, CardHeader } from '../components/common/Card';
import { Button } from '../components/common/Button';
import { Select } from '../components/common/Select';
import { SelfieCapture } from '../components/SelfieCapture';
import { useAuth } from '../context/AuthContext';
import { attendanceService } from '../services/attendanceService';
import { employeeService } from '../services/employeeService';
import { Attendance, AttendanceStatus, AttendanceSummary, Employee, MarkAttendanceData } from '../types';
import api from '../services/api';

// Base status options (Period Leave will be filtered based on gender)
const baseStatusOptions = [
  { value: 'PRESENT', label: 'Present' },
  { value: 'PRESENT_LATE', label: 'Present but Late' },
  { value: 'ABSENT', label: 'Absent' },
  { value: 'HALF_DAY', label: 'Half Day' },
  { value: 'LEAVE_CL', label: 'Casual Leave' },
  { value: 'LEAVE_PL', label: 'Period Leave' },
];

// Helper function to get status options based on gender
const getStatusOptions = (gender?: 'M' | 'F' | 'OTHER'): typeof baseStatusOptions => {
  if (gender === 'M') {
    // Male employees don't have Period Leave
    return baseStatusOptions.filter(option => option.value !== 'LEAVE_PL');
  }
  // Female and Other employees have all options
  return baseStatusOptions;
};

// Helper to check if a date is Sunday
const isSunday = (date: Date): boolean => date.getDay() === 0;

const statusColors: Record<AttendanceStatus, string> = {
  PRESENT: 'bg-success-500',
  PRESENT_LATE: 'bg-yellow-500',
  ABSENT: 'bg-danger-500',
  HALF_DAY: 'bg-warning-500',
  LEAVE_CL: 'bg-primary-500',
  LEAVE_PL: 'bg-purple-500',
  LEAVE_UNPAID: 'bg-orange-500',
  HOLIDAY: 'bg-gray-500', // Keep for backward compatibility with existing records
};

const statusLabels: Record<AttendanceStatus, string> = {
  PRESENT: 'Present',
  PRESENT_LATE: 'Present but Late',
  ABSENT: 'Absent',
  HALF_DAY: 'Half Day',
  LEAVE_CL: 'CL',
  LEAVE_PL: 'PL',
  LEAVE_UNPAID: 'Unpaid',
  HOLIDAY: 'Holiday', // Keep for backward compatibility with existing records
};

export function AttendancePage() {
  const { isHR } = useAuth();
  // Default to January 2026 to show imported attendance data
  const [selectedDate, setSelectedDate] = useState(new Date(2026, 0, 1));
  const [attendanceRecords, setAttendanceRecords] = useState<Attendance[]>([]);
  const [summary, setSummary] = useState<AttendanceSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  
  // Admin attendance marking state
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [selectedEmployee, setSelectedEmployee] = useState<number | null>(null);
  const [viewingEmployeeAttendance, setViewingEmployeeAttendance] = useState<Attendance[]>([]);
  const [adminAttendanceStatus, setAdminAttendanceStatus] = useState<AttendanceStatus>('PRESENT');
  const [isMarkingEmployeeAttendance, setIsMarkingEmployeeAttendance] = useState(false);
  const [calendarRefreshKey, setCalendarRefreshKey] = useState(0);
  
  // Current employee profile (for non-HR users to check lastWorkingDay)
  const [currentEmployee, setCurrentEmployee] = useState<Employee | null>(null);
  
  // Selfie capture state
  const [showSelfieCapture, setShowSelfieCapture] = useState(false);
  const [isUploadingSelfie, setIsUploadingSelfie] = useState(false);
  
  // Attendance details modal state
  const [showAttendanceDetails, setShowAttendanceDetails] = useState(false);
  const [selectedAttendance, setSelectedAttendance] = useState<Attendance | null>(null);
  const [selfieImageUrl, setSelfieImageUrl] = useState<string | null>(null);

  const month = selectedDate.getMonth() + 1;
  const year = selectedDate.getFullYear();

  const fetchEmployees = async () => {
    try {
      const response = await employeeService.getAll(1, 100, { isActive: true });
      setEmployees(response.data);
      
      // Set first employee as default if no employee is selected
      if (response.data.length > 0 && !selectedEmployee) {
        setSelectedEmployee(response.data[0].id);
      }
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const fetchCurrentEmployee = async () => {
    try {
      const profile = await employeeService.getMyProfile();
      setCurrentEmployee(profile);
    } catch (error) {
      console.error('Error fetching current employee profile:', error);
    }
  };

  // Helper function to calculate summary from records
  const calculateSummaryFromRecords = (records: Attendance[]): AttendanceSummary => {
    const presentCount = records.filter(r => r.status === 'PRESENT' || r.status === 'PRESENT_LATE').length;
    const absentCount = records.filter(r => r.status === 'ABSENT').length;
    const halfDayCount = records.filter(r => r.status === 'HALF_DAY').length;
    const leaveCLCount = records.filter(r => r.status === 'LEAVE_CL').length;
    const leavePLCount = records.filter(r => r.status === 'LEAVE_PL').length;
    const leaveUnpaidCount = records.filter(r => r.status === 'LEAVE_UNPAID').length;
    // Count existing HOLIDAY records (for backward compatibility) as present
    const holidayCount = records.filter(r => r.status === 'HOLIDAY').length;
    
    // Count all Sundays in the month (they are counted as present)
    const totalDaysInMonth = new Date(year, month, 0).getDate();
    let sundaysInMonth = 0;
    const sundayDates = new Set<string>();
    
    for (let day = 1; day <= totalDaysInMonth; day++) {
      const date = new Date(year, month - 1, day);
      if (date.getDay() === 0) { // Sunday
        sundaysInMonth++;
        const dateStr = formatLocalDate(date);
        sundayDates.add(dateStr);
      }
    }
    
    // Count how many Sundays already have records
    const recordedSundays = records.filter(r => {
      const recordDate = typeof r.date === 'string' 
        ? r.date.split('T')[0] 
        : formatLocalDate(new Date(r.date));
      return sundayDates.has(recordDate);
    }).length;
    
    // Add missing Sundays to present count (Sundays count as present)
    const missingSundays = sundaysInMonth - recordedSundays;
    
    // Calculate total present days: half days count as 0.5, Sundays count as present
    const totalDays = presentCount + (halfDayCount * 0.5) + 
                      leaveCLCount + leavePLCount + leaveUnpaidCount + 
                      holidayCount + missingSundays; // Include holidays and missing Sundays as present
    
    return {
      present: presentCount + holidayCount + missingSundays, // Include holidays and missing Sundays
      absent: absentCount,
      halfDay: halfDayCount,
      leaveCL: leaveCLCount,
      leavePL: leavePLCount,
      leaveUnpaid: leaveUnpaidCount,
      holiday: 0, // No longer using holiday count separately
      total: Math.round(totalDays * 10) / 10, // Round to 1 decimal place
    };
  };

  const fetchAttendance = async () => {
    setIsLoading(true);
    try {
      const records = await attendanceService.getMyAttendance({ month, year });
      setAttendanceRecords(records);
      
      // Recalculate summary from records to ensure it includes Sundays and updates immediately
      const adjustedSummary = calculateSummaryFromRecords(records);
      setSummary(adjustedSummary);
    } catch (error) {
      console.error('Error fetching attendance:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Helper to format date as YYYY-MM-DD using local timezone
  const formatLocalDate = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const fetchEmployeeAttendance = async () => {
    if (!selectedEmployee) return;
    
    setIsLoading(true);
    try {
      const records = await attendanceService.getAllAttendance({ 
        month, 
        year, 
        employeeId: selectedEmployee 
      });
      setViewingEmployeeAttendance(records);
      
      // Calculate summary from records using the helper function
      const summaryData = calculateSummaryFromRecords(records);
      setSummary(summaryData);
    } catch (error) {
      console.error('Error fetching employee attendance:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleMarkAttendance = async () => {
    // Employees can only use selfie-based attendance
    if (!isHR) {
      setShowSelfieCapture(true);
      return;
    }
    
    // HR/Admin should use the "Mark Employee Attendance" section instead
    toast.error('Please use the "Mark Employee Attendance" section above to mark attendance for employees.');
  };

  const handleSelfieCapture = async (
    file: File,
    location: { latitude: number; longitude: number },
    timestamp: Date
  ) => {
    setIsUploadingSelfie(true);
    try {
      await attendanceService.markAttendanceWithSelfie(file, location, timestamp);
      toast.success('Attendance marked successfully with selfie!');
      setShowSelfieCapture(false);
      // Refresh attendance and summary immediately
      await fetchAttendance();
      // Force calendar re-render
      setCalendarRefreshKey(prev => prev + 1);
    } catch (error) {
      // Error handled by interceptor
    } finally {
      setIsUploadingSelfie(false);
    }
  };

  const handleMarkEmployeeAttendance = async () => {
    if (!selectedEmployee) {
      toast.error('Please select an employee');
      return;
    }
    
    setIsMarkingEmployeeAttendance(true);
    try {
      // Use the selected date from calendar (formatLocalDate handles timezone correctly)
      const dateStr = formatLocalDate(selectedDate);
      const attendanceData: MarkAttendanceData = {
        date: dateStr,
        status: adminAttendanceStatus,
      };
      
      await attendanceService.markAttendanceForEmployee(selectedEmployee, attendanceData);
      toast.success('Attendance marked successfully!');
      
      // Refresh employee attendance and summary immediately
      await fetchEmployeeAttendance();
      
      // Force calendar re-render by incrementing refresh key
      setCalendarRefreshKey(prev => prev + 1);
    } catch (error) {
      // Error handled by interceptor
    } finally {
      setIsMarkingEmployeeAttendance(false);
    }
  };

  useEffect(() => {
    if (isHR) {
      fetchEmployees();
      if (selectedEmployee) {
        fetchEmployeeAttendance();
      }
    } else {
      fetchCurrentEmployee();
      fetchAttendance();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month, year, isHR]);

  useEffect(() => {
    if (isHR && selectedEmployee) {
      fetchEmployeeAttendance();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEmployee, month, year]);

  // Reset status if Period Leave is selected but employee is male
  useEffect(() => {
    const selectedEmp = employees.find(e => e.id === selectedEmployee);
    if (isHR && selectedEmp && selectedEmp.gender === 'M' && adminAttendanceStatus === 'LEAVE_PL') {
      setAdminAttendanceStatus('PRESENT');
    }
  }, [selectedEmployee, employees, isHR, adminAttendanceStatus]);

  // Removed selectedStatus useEffect for employees since they can only use selfie-based attendance

  const getAttendanceForDate = (date: Date): Attendance | undefined => {
    // Use local date components to match correctly
    const dateStr = formatLocalDate(date);
    // For admin viewing employee attendance, use viewingEmployeeAttendance
    // For regular employees, use attendanceRecords
    const recordsToSearch = isHR && selectedEmployee ? viewingEmployeeAttendance : attendanceRecords;
    
    // Match date - handle both ISO string format and date object
    return recordsToSearch.find((a) => {
      const attendanceDate = typeof a.date === 'string' 
        ? a.date.split('T')[0] 
        : formatLocalDate(new Date(a.date));
      return attendanceDate === dateStr;
    });
  };

  const tileClassName = ({ date, view }: { date: Date; view: string }) => {
    if (view !== 'month') return '';
    
    // Sundays are counted as present
    if (isSunday(date)) {
      return 'attendance-present';
    }
    
    const attendance = getAttendanceForDate(date);
    if (attendance) {
      return `attendance-${attendance.status.toLowerCase()}`;
    }
    return '';
  };

  // Get tooltip text for a date
  const getTooltipText = (date: Date): string => {
    if (isSunday(date)) {
      return 'Sunday - Weekly Holiday (Present by default)';
    }
    
    const attendance = getAttendanceForDate(date);
    if (attendance) {
      const statusLabel = statusLabels[attendance.status];
      let tooltip = `Status: ${statusLabel}`;
      
      if (attendance.checkIn) {
        const checkInTime = new Date(attendance.checkIn).toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit',
        });
        tooltip += `\nCheck-in: ${checkInTime}`;
      }
      
      if (attendance.lateMinutes) {
        tooltip += `\nLate: ${attendance.lateMinutes} minutes`;
      }
      
      if (attendance.selfiePath && isHR) {
        tooltip += '\n📷 Click to view selfie and location';
      }
      
      return tooltip;
    }
    
    return 'No attendance recorded';
  };

  const tileContent = ({ date, view }: { date: Date; view: string }) => {
    if (view !== 'month') return null;
    
    const attendance = getAttendanceForDate(date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dateOnly = new Date(date);
    dateOnly.setHours(0, 0, 0, 0);
    const isFuture = dateOnly > today;
    
    // Check if date is after lastWorkingDay
    let isAfterLastWorkingDay = false;
    if (isHR && selectedEmployee) {
      const selectedEmp = employees.find(e => e.id === selectedEmployee);
      if (selectedEmp?.lastWorkingDay) {
        const lastWorkingDay = new Date(selectedEmp.lastWorkingDay);
        lastWorkingDay.setHours(0, 0, 0, 0);
        isAfterLastWorkingDay = dateOnly > lastWorkingDay;
      }
    } else if (!isHR && currentEmployee?.lastWorkingDay) {
      // For regular employees, check their lastWorkingDay
      const lastWorkingDay = new Date(currentEmployee.lastWorkingDay);
      lastWorkingDay.setHours(0, 0, 0, 0);
      isAfterLastWorkingDay = dateOnly > lastWorkingDay;
    }
    
    // If date is after lastWorkingDay, mark as ABSENT (including Sundays)
    if (isAfterLastWorkingDay) {
      return (
        <div 
          className="flex justify-center mt-1"
          title={attendance?.notes || `Absent - After last working day`}
        >
          <span className={`w-2 h-2 rounded-full ${statusColors.ABSENT}`} />
        </div>
      );
    }
    
    // Sundays: Show as PRESENT by default (not future), but allow admin to mark as ABSENT
    if (isSunday(date)) {
      // If there's an explicit attendance record, use it
      if (attendance) {
        return (
          <div 
            className="flex justify-center mt-1"
            title={getTooltipText(date)}
          >
            <span className={`w-2 h-2 rounded-full ${statusColors[attendance.status] || statusColors.PRESENT}`} />
          </div>
        );
      }
      // For future Sundays, don't show anything
      if (isFuture) {
        return null;
      }
      // For past/present Sundays without record, show as PRESENT
      return (
        <div 
          className="flex justify-center mt-1"
          title="Sunday - Weekly Holiday (Present by default)"
        >
          <span className={`w-2 h-2 rounded-full ${statusColors.PRESENT}`} />
        </div>
      );
    }
    if (attendance) {
      return (
        <div 
          className={`flex justify-center mt-1 ${isHR ? "cursor-pointer hover:opacity-80" : ""}`}
          onClick={async (e) => {
            e.stopPropagation();
            if (isHR) {
              setSelectedAttendance(attendance);
              setShowAttendanceDetails(true);
              
              // Load selfie image with authentication
              if (attendance.selfiePath && attendance.id) {
                try {
                  // Clean up previous blob URL if exists
                  if (selfieImageUrl) {
                    URL.revokeObjectURL(selfieImageUrl);
                    setSelfieImageUrl(null);
                  }
                  
                  const response = await api.get(`/attendance/${attendance.id}/selfie`, {
                    responseType: 'blob',
                  });
                  const blob = new Blob([response.data], { type: 'image/jpeg' });
                  const imageUrl = URL.createObjectURL(blob);
                  setSelfieImageUrl(imageUrl);
                } catch (error) {
                  console.error('Failed to load selfie image:', error);
                  setSelfieImageUrl('data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="300"%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%23999"%3EImage not found%3C/text%3E%3C/svg%3E');
                }
              }
            }
          }}
          title={getTooltipText(date)}
        >
          <span className={`w-2 h-2 rounded-full ${statusColors[attendance.status]}`} />
          {isHR && attendance.selfiePath && (
            <span className="ml-1 text-xs">📷</span>
          )}
        </div>
      );
    }
    
    // No attendance record - still show tooltip for admin
    return (
      <div 
        className={`flex justify-center mt-1 ${isHR ? "cursor-pointer" : ""}`}
        onClick={(e) => {
          e.stopPropagation();
          if (isHR) {
            // Show details even if no attendance record
            setSelectedAttendance({
              id: 0,
              employeeId: selectedEmployee || 0,
              date: formatLocalDate(date),
              status: 'ABSENT' as AttendanceStatus,
            } as Attendance);
            setShowAttendanceDetails(true);
          }
        }}
        title={getTooltipText(date)}
      />
    );
  };

  return (
    <div className="space-y-6 animate-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Attendance</h1>
          <p className="text-gray-500">
            {isHR ? 'Track and manage employee attendance' : 'Track and manage your attendance'}
          </p>
        </div>
      </div>

      {/* Admin: Select Employee to View/Mark Attendance */}
      {isHR && (
        <Card>
          <CardHeader title="Mark Employee Attendance" />
          <div className="grid grid-cols-3 gap-4">
            <Select
              label="Select Employee"
              value={selectedEmployee ? String(selectedEmployee) : ''}
              onChange={(e) => {
                const empId = e.target.value ? parseInt(e.target.value) : null;
                setSelectedEmployee(empId);
              }}
              options={employees.map(emp => ({
                value: String(emp.id),
                label: `${emp.firstName} ${emp.lastName} (${emp.employeeCode})`,
              }))}
            />
            
            <Select
              label="Status"
              value={adminAttendanceStatus}
              onChange={(e) => setAdminAttendanceStatus(e.target.value as AttendanceStatus)}
              options={useMemo(() => {
                const selectedEmp = employees.find(e => e.id === selectedEmployee);
                const baseOptions = getStatusOptions(selectedEmp?.gender);
                // Create a new array with updated labels, ensuring unique values
                return baseOptions.map(option => 
                  isSunday(selectedDate) && option.value === 'ABSENT'
                    ? { ...option, label: 'Absent (Override Sunday)' }
                    : option
                );
              }, [selectedEmployee, employees, selectedDate])}
            />
            
            <Button
              onClick={handleMarkEmployeeAttendance}
              isLoading={isMarkingEmployeeAttendance}
              disabled={!selectedEmployee}
              className="w-full"
            >
              Mark Attendance
            </Button>
            {isSunday(selectedDate) && (
              <div className="col-span-3 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-800">
                <p>⚠️ Sunday is marked as Present by default. You can override by selecting "Absent" above.</p>
              </div>
            )}
          </div>
          <p className="text-xs text-gray-400 text-center mt-3">
            Select a date from the calendar to mark attendance for that date
          </p>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Calendar */}
        <div className="lg:col-span-2">
          <Card>
            <div className="flex justify-between items-center p-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900">
                {isHR && selectedEmployee 
                  ? `Attendance Calendar - ${employees.find(e => e.id === selectedEmployee)?.firstName} ${employees.find(e => e.id === selectedEmployee)?.lastName}`
                  : "Attendance Calendar"}
              </h3>
              <Button
                variant="outline"
                size="sm"
                onClick={async () => {
                  if (isHR && selectedEmployee) {
                    await fetchEmployeeAttendance();
                  } else {
                    await fetchAttendance();
                  }
                  setCalendarRefreshKey(prev => prev + 1);
                  toast.success('Calendar refreshed');
                }}
              >
                🔄 Refresh
              </Button>
            </div>
            <Calendar
              key={`calendar-${selectedEmployee}-${calendarRefreshKey}-${month}-${year}`}
              onChange={(value) => setSelectedDate(value as Date)}
              value={selectedDate}
              tileClassName={tileClassName}
              tileContent={tileContent}
              className="w-full"
            />
            
            {/* Legend */}
            <div className="mt-4 pt-4 border-t flex flex-wrap gap-4">
              {Object.entries(statusLabels)
                .filter(([status]) => status !== 'LEAVE_UNPAID' && status !== 'HOLIDAY') // Remove Unpaid Leave and Holiday from legend
                .map(([status, label]) => (
                  <div key={status} className="flex items-center gap-2">
                    <span className={`w-3 h-3 rounded-full ${statusColors[status as AttendanceStatus]}`} />
                    <span className="text-sm text-gray-600">{label}</span>
                  </div>
                ))}
            </div>
          </Card>
        </div>

        {/* Mark Attendance & Summary */}
        {!isHR || !selectedEmployee ? (
          <div className="space-y-6">
            <Card>
              <CardHeader title="Mark Attendance" />
            <div className="space-y-4">
              <div className={`p-3 rounded-lg text-center ${isSunday(selectedDate) ? 'bg-gray-200' : 'bg-gray-50'}`}>
                <p className="text-sm text-gray-500">Selected Date</p>
                <p className="text-lg font-semibold">
                  {selectedDate.toLocaleDateString('en-US', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                </p>
                {isSunday(selectedDate) && (
                  <p className="text-sm text-gray-600 mt-1 font-medium">
                    🏖️ Sunday - Weekly Holiday (Present by default, Admin can mark as Absent)
                  </p>
                )}
              </div>
              {isSunday(selectedDate) && !isHR ? (
                <div className="p-4 bg-gray-100 rounded-lg text-center">
                  <p className="text-gray-600">
                    Sundays are automatically marked as present.
                  </p>
                  <p className="text-sm text-gray-500 mt-1">
                    They count as present days for payroll purposes.
                  </p>
                </div>
              ) : (
                <>
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-800 font-medium mb-2">
                      📸 Selfie-Based Attendance
                    </p>
                    <p className="text-xs text-blue-600">
                      Click "Mark Attendance" to take a selfie. Your attendance will be automatically marked based on your arrival time and location.
                    </p>
                  </div>
                  <Button
                    className="w-full"
                    onClick={handleMarkAttendance}
                    isLoading={isUploadingSelfie}
                  >
                    {isUploadingSelfie ? 'Uploading...' : 'Mark Attendance with Selfie'}
                  </Button>
                </>
              )}
            </div>
          </Card>

          <Card>
            <CardHeader
              title="Monthly Summary"
              subtitle={new Date(year, month - 1).toLocaleDateString('en-US', {
                month: 'long',
                year: 'numeric',
              })}
            />
            {isLoading ? (
              <div className="flex justify-center py-4">
                <div className="spinner" />
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Present</span>
                  <span className="font-semibold text-success-600">{summary?.present || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Absent</span>
                  <span className="font-semibold text-danger-600">{summary?.absent || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Half Days</span>
                  <span className="font-semibold text-warning-600">{summary?.halfDay || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Casual Leave</span>
                  <span className="font-semibold text-primary-600">{summary?.leaveCL || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Period Leave</span>
                  <span className="font-semibold text-purple-600">{summary?.leavePL || 0}</span>
                </div>
                <div className="border-t pt-3 space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="font-semibold text-gray-900">Total Present</span>
                    <span className="font-bold text-lg">
                      {typeof summary?.total === 'number' && summary.total % 1 !== 0 
                        ? summary.total.toFixed(1) 
                        : summary?.total || 0}
                    </span>
                  </div>
                  <div className="pt-2 border-t">
                    <div className="text-xs text-gray-500 mb-2">Calculation:</div>
                    <div className="text-xs text-gray-600 space-y-1">
                        <div className="space-y-0.5">
                          <div className="flex justify-between">
                            <span>Present:</span>
                            <span className="font-medium">{summary?.present || 0}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Half Days (× 0.5):</span>
                            <span className="font-medium">{((summary?.halfDay || 0) * 0.5).toFixed(1)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Casual Leave:</span>
                            <span className="font-medium">{summary?.leaveCL || 0}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Period Leave:</span>
                            <span className="font-medium">{summary?.leavePL || 0}</span>
                          </div>
                        </div>
                      <div className="pt-1 border-t flex justify-between font-semibold">
                        <span>Total Present:</span>
                        <span>
                          {((summary?.present || 0) + ((summary?.halfDay || 0) * 0.5) + 
                            (summary?.leaveCL || 0) + (summary?.leavePL || 0)).toFixed(1)}
                        </span>
                      </div>
                      <div className="flex justify-between pt-1">
                        <span>Total Days in Month:</span>
                        <span className="font-medium">
                          {new Date(year, month, 0).getDate()}
                        </span>
                      </div>
                      {Math.abs(((summary?.present || 0) + ((summary?.halfDay || 0) * 0.5) + 
                        (summary?.leaveCL || 0) + (summary?.leavePL || 0)) - new Date(year, month, 0).getDate()) > 0.1 && (
                        <div className="flex justify-between text-warning-600 pt-1">
                            <span>Absent Days:</span>
                          <span className="font-medium">
                            {(new Date(year, month, 0).getDate() - 
                             ((summary?.present || 0) + ((summary?.halfDay || 0) * 0.5) + 
                              (summary?.leaveCL || 0) + (summary?.leavePL || 0))).toFixed(1)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </Card>
        </div>
        ) : (
          <div className="space-y-6">
            <Card>
              <CardHeader 
                title="Monthly Summary"
                subtitle={new Date(year, month - 1).toLocaleDateString('en-US', {
                  month: 'long',
                  year: 'numeric',
                })}
              />
              {isLoading ? (
                <div className="flex justify-center py-4">
                  <div className="spinner" />
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Present</span>
                    <span className="font-semibold text-success-600">{summary?.present || 0}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Absent</span>
                    <span className="font-semibold text-danger-600">{summary?.absent || 0}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Half Days</span>
                    <span className="font-semibold text-warning-600">{summary?.halfDay || 0}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Casual Leave</span>
                    <span className="font-semibold text-primary-600">{summary?.leaveCL || 0}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Period Leave</span>
                    <span className="font-semibold text-purple-600">{summary?.leavePL || 0}</span>
                  </div>
                  <div className="border-t pt-3 space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="font-semibold text-gray-900">Total Present</span>
                      <span className="font-bold text-lg">
                        {typeof summary?.total === 'number' && summary.total % 1 !== 0 
                          ? summary.total.toFixed(1) 
                          : summary?.total || 0}
                      </span>
                    </div>
                    <div className="pt-2 border-t">
                      <div className="text-xs text-gray-500 mb-2">Calculation:</div>
                      <div className="text-xs text-gray-600 space-y-1">
                          <div className="space-y-0.5">
                            <div className="flex justify-between">
                              <span>Present:</span>
                              <span className="font-medium">{summary?.present || 0}</span>
                            </div>
                            <div className="flex justify-between">
                              <span>Half Days (× 0.5):</span>
                              <span className="font-medium">{((summary?.halfDay || 0) * 0.5).toFixed(1)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span>Casual Leave:</span>
                              <span className="font-medium">{summary?.leaveCL || 0}</span>
                            </div>
                            <div className="flex justify-between">
                              <span>Period Leave:</span>
                              <span className="font-medium">{summary?.leavePL || 0}</span>
                            </div>
                          </div>
                        <div className="pt-1 border-t flex justify-between font-semibold">
                          <span>Total Present:</span>
                          <span>
                            {((summary?.present || 0) + ((summary?.halfDay || 0) * 0.5) + 
                              (summary?.leaveCL || 0) + (summary?.leavePL || 0)).toFixed(1)}
                          </span>
                        </div>
                        <div className="flex justify-between pt-1">
                          <span>Total Days in Month:</span>
                          <span className="font-medium">
                            {new Date(year, month, 0).getDate()}
                          </span>
                        </div>
                        {Math.abs(((summary?.present || 0) + ((summary?.halfDay || 0) * 0.5) + 
                          (summary?.leaveCL || 0) + (summary?.leavePL || 0)) - new Date(year, month, 0).getDate()) > 0.1 && (
                          <div className="flex justify-between text-warning-600 pt-1">
                              <span>Absent Days:</span>
                            <span className="font-medium">
                              {(new Date(year, month, 0).getDate() - 
                               ((summary?.present || 0) + ((summary?.halfDay || 0) * 0.5) + 
                                (summary?.leaveCL || 0) + (summary?.leavePL || 0))).toFixed(1)}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </Card>
          </div>
        )}
      </div>

      {/* Selfie Capture Modal */}
      {showSelfieCapture && !isHR && (
        <SelfieCapture
          onCapture={handleSelfieCapture}
          onClose={() => setShowSelfieCapture(false)}
        />
      )}

      {/* Attendance Details Modal (Admin/HR) */}
      {showAttendanceDetails && selectedAttendance && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Attendance Details</h3>
                <button
                  onClick={() => {
                    setShowAttendanceDetails(false);
                    setSelectedAttendance(null);
                    // Clean up blob URL when closing modal
                    if (selfieImageUrl) {
                      URL.revokeObjectURL(selfieImageUrl);
                      setSelfieImageUrl(null);
                    }
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <p className="text-sm text-gray-500">Date</p>
                  <p className="font-medium">
                    {new Date(selectedAttendance.date).toLocaleDateString('en-US', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </p>
                </div>

                <div>
                  <p className="text-sm text-gray-500">Status</p>
                  <p className="font-medium">
                    <span className={`inline-block px-2 py-1 rounded text-sm text-white ${statusColors[selectedAttendance.status]}`}>
                      {statusLabels[selectedAttendance.status]}
                    </span>
                  </p>
                </div>

                {selectedAttendance.checkIn && (
                  <div>
                    <p className="text-sm text-gray-500">Check-in Time</p>
                    <p className="font-medium">
                      {new Date(selectedAttendance.checkIn).toLocaleTimeString('en-US', {
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit',
                      })}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      {new Date(selectedAttendance.checkIn).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })}
                    </p>
                  </div>
                )}

                {selectedAttendance.lateMinutes && (
                  <div>
                    <p className="text-sm text-gray-500">Late Minutes</p>
                    <p className="font-medium">{selectedAttendance.lateMinutes} minutes</p>
                  </div>
                )}

                {selectedAttendance.selfiePath && (
                  <div>
                    <p className="text-sm text-gray-500 mb-2">Selfie</p>
                    <div className="border rounded-lg overflow-hidden">
                      {selfieImageUrl ? (
                        <img
                          src={selfieImageUrl}
                          alt="Attendance selfie"
                          className="w-full h-auto"
                          onError={() => {
                            setSelfieImageUrl('data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="300"%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%23999"%3EImage not found%3C/text%3E%3C/svg%3E');
                          }}
                        />
                      ) : (
                        <div className="flex items-center justify-center h-48 bg-gray-100">
                          <div className="text-center">
                            <div className="spinner w-8 h-8 border-4 border-primary-600 border-t-transparent mx-auto mb-2" />
                            <p className="text-sm text-gray-500">Loading image...</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {(selectedAttendance.latitude && selectedAttendance.longitude) && (
                  <div>
                    <p className="text-sm text-gray-500 mb-2">Location</p>
                    <div className="space-y-2">
                      <p className="font-medium">
                        Latitude: {selectedAttendance.latitude.toFixed(6)}
                      </p>
                      <p className="font-medium">
                        Longitude: {selectedAttendance.longitude.toFixed(6)}
                      </p>
                      <a
                        href={`https://www.google.com/maps?q=${selectedAttendance.latitude},${selectedAttendance.longitude}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-primary-600 hover:text-primary-800 text-sm"
                      >
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        View on Google Maps
                      </a>
                    </div>
                  </div>
                )}

                {selectedAttendance.notes && (
                  <div>
                    <p className="text-sm text-gray-500">Notes</p>
                    <p className="font-medium">{selectedAttendance.notes}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
